/*
 * data-action delegation. View markup names a handler by dotted path and may
 * pass a JSON array of arguments:
 *   <span data-action="Person.List.DisplayCreateModal">
 *   <tr   data-action="Person.List.ShowDetail" data-args="[@person.Id]">
 * The handler runs with `this` bound to the element; returning false
 * suppresses the default action and propagation. Click serves everything
 * except selects, textareas, toggle inputs and file pickers, which use
 * change (a real toggle fires both, and dispatching twice was the original
 * bug; a file picker's click is the dialog opening, not a choice).
 */
import { needsLoad, resolveAction, resolveActionSync, type ActionHandler } from './registry';

let bound = false;
const onClick = (event: Event): void => dispatch(event, false);
const onChange = (event: Event): void => dispatch(event, true);

/**
 * jQuery's .data() auto-typing, which the legacy markup relies on: "true" /
 * "false" / "null" / a number / JSON-looking text become values, the rest
 * stays a string.
 */
export function parseDataValue(raw: string | null | undefined): unknown {
    if (raw === null || raw === undefined) return undefined;
    const text = raw.trim();
    if (text === 'true') return true;
    if (text === 'false') return false;
    if (text === 'null') return null;
    if (/^-?\d+(\.\d+)?$/.test(text) && String(Number(text)) === text) return Number(text);
    if (/^[[{]/.test(text)) {
        try { return JSON.parse(text); } catch { return raw; }
    }
    return raw;
}

/** Arguments for a handler: the data-args array, a single value wrapped, or none. */
export function argsOf(element: Element): unknown[] {
    const raw = parseDataValue(element.getAttribute('data-args'));
    if (raw === undefined) return [];
    return Array.isArray(raw) ? raw : [raw];
}

const changeControls = 'select[data-action], textarea[data-action], input[type="checkbox"][data-action], input[type="radio"][data-action], input[type="file"][data-action]';

function isChangeControl(element: Element): boolean {
    return element.matches(changeControls);
}

function invoke(handler: ActionHandler, element: Element, event: Event): void {
    const result = handler.apply(element, argsOf(element));
    if (result === false) {
        event.preventDefault();
        event.stopPropagation();
    }
}

/**
 * A control the operator is not allowed to use.
 *
 * Permission gating renders controls DISABLED rather than absent, so a
 * greyed-out button says which permission is missing instead of just not being
 * there. The browser withholds click from a disabled form control, but nothing
 * withholds it from a programmatic `dispatchEvent`, from `aria-disabled` (which
 * is only ever advisory), or from a `.disabled` class on an anchor -- and every
 * one of those reaches this delegator as an ordinary click. The gate is the
 * server's either way; this keeps the disabled state from being a lie.
 */
function isDisabled(element: Element): boolean {
    if ((element as { disabled?: boolean }).disabled === true) return true;
    if (element.getAttribute('aria-disabled') === 'true') return true;
    if (element.classList.contains('disabled')) return true;
    return element.closest('fieldset[disabled]') !== null;
}

function dispatch(event: Event, wantChange: boolean): void {
    const origin = event.target;
    if (!(origin instanceof Element)) return;
    const element = origin.closest('[data-action]');
    if (!element) return;
    if (isDisabled(element)) return;
    if (isChangeControl(element) !== wantChange) return;

    const path = element.getAttribute('data-action');
    const handler = resolveActionSync(path);
    if (handler) {
        invoke(handler, element, event);
        return;
    }
    if (path && needsLoad(path)) {
        // The area has to be imported first, so the verdict on the default
        // action cannot be synchronous. An href="#" jump is the only default
        // worth pre-empting; anything else keeps its native behaviour.
        if (element instanceof HTMLAnchorElement && (!element.getAttribute('href') || element.getAttribute('href') === '#')) {
            event.preventDefault();
        }
        void resolveAction(path).then(loaded => {
            if (loaded) invoke(loaded, element, event);
            else console.warn('[data-action] unresolved:', path);
        });
        return;
    }
    console.warn('[data-action] unresolved:', path);
}

/** Install the delegated listeners once; safe to call repeatedly. */
export function initDispatch(): void {
    if (bound) return;
    bound = true;
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
}

/** Test support. */
export function resetDispatch(): void {
    document.removeEventListener('click', onClick);
    document.removeEventListener('change', onChange);
    bound = false;
}
